import { useCallback, useSyncExternalStore } from "react";
import { useCurrentUser } from "../../context/user";

const changedEvent = "uptain:last-visited-shop-changed";
const subscribe = (listener: () => void) => {
  window.addEventListener("storage", listener);
  window.addEventListener(changedEvent, listener);
  return () => {
    window.removeEventListener("storage", listener);
    window.removeEventListener(changedEvent, listener);
  };
};

// A navigation preference, never the current shop or an authorization decision.
export const useLastVisitedShopId = () => {
  const { id } = useCurrentUser();
  const key = "uptain:last-visited-shop:v1:" + encodeURIComponent(id);
  const read = useCallback(() => {
    try {
      const value = window.localStorage.getItem(key);
      const shopId = value === null ? undefined : Number(value);
      return shopId !== undefined && Number.isSafeInteger(shopId) && shopId > 0
        ? shopId
        : undefined;
    } catch {
      return undefined;
    }
  }, [key]);
  const lastVisitedShopId = useSyncExternalStore(subscribe, read, () => undefined);
  const rememberShop = useCallback(
    (shopId: number) => {
      if (!Number.isSafeInteger(shopId) || shopId <= 0) return;
      try {
        window.localStorage.setItem(key, String(shopId));
        window.dispatchEvent(new Event(changedEvent));
      } catch {
        // Disabled/full storage must not prevent shop navigation.
      }
    },
    [key]
  );
  return { lastVisitedShopId, rememberShop };
};
